/**
 * "Your runs" (`/runs`): one person, many runs. Every run is a membership in a cohort; each row names the cohort, the person's part in it, where it
 * stands and one plain way in. The run opened last on this device comes first. The memberships carry the cohort, the role and the team only, so the
 * name and the state are read per run from the views the person already has (the welcome, the board, the team, the solo list).
 */
import type { Membership } from "@bsim/contracts";
import { useRoute } from "../api/context";
import { Link } from "../shell/history";
import { lastRun, rememberRun, runPath } from "../shell/runs";
import { comparableGroups } from "./compare";
import { standingWords } from "../score/labels";
import { roleLabel } from "../shell/TopBar";
import { SoloFrame } from "../solo/SoloFrame";
import { GxPill } from "../theme/GxPill";
import { GxCard, GxWaiting } from "../theme/primitives";
import "./runs.css";

interface Line { readonly name: string | null; readonly state: string | null; readonly finished: boolean; /** A solo run's score and rank so far (`soloList` standing), in words; null for any other row or before round 1 is released. */ readonly standing?: string | null }

const partWords = (m: Membership): string =>
  m.role === "facilitator" ? "You facilitate it" : m.role === "observer" ? "You observe it" : m.cohortMode === "solo" ? "Your own run" : m.teamRole === null ? "Workshop" : `Workshop, ${roleLabel(m.teamRole)}`;

/** Where a finished run opens: a look back, never a way to change it. */
const lookBackPath = (m: Membership): string =>
  m.role === "facilitator" ? `/f/${m.cohortId}/board` : m.cohortMode === "solo" ? `/solo/${m.cohortId}/final` : `/c/${m.cohortId}/results`;

function useLine(m: Membership, solo: ReturnType<typeof useRoute<"soloList">>["data"]): Line {
  const facilitator = m.role === "facilitator";
  const welcome = useRoute("welcomeCard", facilitator ? null : { cohortId: m.cohortId }, { staleTime: Infinity });
  const board = useRoute("cohortBoard", facilitator ? { cohortId: m.cohortId } : null);
  const team = useRoute("teamState", !facilitator && m.cohortMode !== "solo" && m.teamId !== null ? { teamId: m.teamId } : null);
  if (facilitator) {
    const status = board.data?.cohort.status;
    const round = board.data?.rounds.find((r) => r.id === board.data?.currentRoundId);
    return { name: board.data?.cohort.name ?? null, finished: status === "completed" || status === "archived",
      state: status === undefined ? null : status === "setup" ? "Not started" : status === "active" ? (round === undefined ? "Running" : `Running, round ${round.roundNo}`) : "Finished" };
  }
  const name = welcome.data?.cohortName ?? null;
  if (m.cohortMode === "solo") {
    if (solo === undefined) return { name, state: null, finished: false };
    const run = solo.runs.find((r) => r.runId === m.cohortId);
    if (run === undefined) return { name, state: "Not started", finished: false };
    const finished = run.phase === "completed";
    const standing = run.standing == null ? null : standingWords(run.standing, finished);
    const ownName = run.name ?? name;
    return finished ? { name: ownName, state: "Finished", finished, standing } : { name: ownName, state: run.roundNo === 0 ? "Setting up the bank" : `Round ${run.roundNo}`, finished, standing };
  }
  const round = team.data?.round;
  return { name, finished: false, state: m.teamId === null ? "Waiting for a team" : team.data === undefined ? null : round === null || round === undefined ? "Not started" : `Round ${round.roundNo}` };
}

function RunRow({ m, current, solo, userId }: { readonly m: Membership; readonly current: boolean; readonly solo: ReturnType<typeof useRoute<"soloList">>["data"]; readonly userId: string }) {
  const line = useLine(m, solo);
  const href = line.finished ? lookBackPath(m) : runPath(m, true);
  return (
    <li className="gx-rl-row" data-testid={`runs.row.${m.cohortId}`}>
      <div className="gx-rl-main">
        <h2 className="gx-h2" data-testid="runs.name">{line.name ?? "Your run"}</h2>
        <p className="gx-small gx-muted">{partWords(m)}{line.state === null ? "" : ` · ${line.state}`}</p>
        {line.standing == null ? null : <p className="gx-small gx-num" data-testid="runs.standing">{line.standing}</p>}
      </div>
      <div className="gx-rl-acts">
        {current ? <GxPill tone="neutral" testId="runs.last">Opened last</GxPill> : null}
        {line.finished && m.cohortMode === "solo" && m.role !== "facilitator"
          ? <Link href={`/solo?run=${m.cohortId}`} className="gx-btn gx-btn--secondary" data-testid="runs.another" onClick={() => rememberRun(userId, m.cohortId)}>Start another run</Link> : null}
        <Link href={href} className={`gx-btn ${line.finished ? "gx-btn--secondary" : "gx-btn--primary"}`} data-testid="runs.open" onClick={() => rememberRun(userId, m.cohortId)}>{line.finished ? "Look back" : "Open"}</Link>
      </div>
    </li>
  );
}

export function RunsPage({ memberships, userId }: { readonly memberships: readonly Membership[]; readonly userId: string }) {
  const last = lastRun(userId);
  const ordered = [...memberships].sort((a, b) => Number(b.cohortId === last) - Number(a.cohortId === last));
  const solo = useRoute("soloList", memberships.some((m) => m.cohortMode === "solo" && m.role !== "facilitator") ? undefined : null);
  return (
    <SoloFrame bankName="Governexus" subline="Your runs" title="Your runs">
      <div className="gx-runs" data-testid="runs">
        <div className="gx-pagehead"><div><div className="gx-eyebrow">Your runs</div><h1 className="gx-h1">Which run do you want to open?</h1></div></div>
        <p className="gx-muted">You are in more than one run. A finished run stays here to look back on. Choose Switch run in the account menu to come back to this list.</p>
        {comparableGroups(solo.data?.runs ?? []).length === 0 ? null : <p className="gx-cmp-link"><Link href="/runs/compare" data-testid="runs.compare">Compare two finished runs</Link></p>}
        {solo.isLoading ? <GxWaiting what="your runs" height={120} /> : null}
        <GxCard>
          <ul className="gx-rl-list">{ordered.map((m) => <RunRow key={m.cohortId} m={m} current={m.cohortId === last} solo={solo.data} userId={userId} />)}</ul>
        </GxCard>
      </div>
    </SoloFrame>
  );
}
